/**
 * NearbyTenantsPanel — surfaces businesses Google reports near the
 * geocoded property address. These feed into the BOV writeup as
 * trade-area context (NOT rent-roll occupants — brokers add real
 * tenants via the Rent Roll page).
 */

import { useState } from "react";
import { Store, RefreshCw } from "lucide-react";
import {
  lookupNearbyTenants,
  type TenantSuggestion,
} from "../../api/client";
import { useToast } from "../../contexts/ToastContext";
import { friendlyError } from "../../utils/errors";

interface Props {
  dealId: string;
  hasLocation: boolean;
  initialTenants?: TenantSuggestion[];
}

export default function NearbyTenantsPanel({
  dealId,
  hasLocation,
  initialTenants,
}: Props) {
  const toast = useToast();
  const [tenants, setTenants] = useState<TenantSuggestion[]>(initialTenants || []);
  const [loading, setLoading] = useState(false);

  async function handleFetch() {
    if (!hasLocation) {
      toast.error("Look up the location first.");
      return;
    }
    setLoading(true);
    try {
      const result = await lookupNearbyTenants(dealId);
      setTenants(result.tenants);
      if (result.tenants.length === 0) {
        toast.info("No nearby businesses found at that address.");
      }
    } catch (e: any) {
      toast.error(
        friendlyError(
          e?.response?.data?.detail ||
            "Couldn't pull nearby businesses. Try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  if (!hasLocation && tenants.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-bark-800 flex items-center gap-2">
          <Store className="w-4 h-4 text-forest-700" />
          Nearby Businesses
        </h3>
        <button
          onClick={handleFetch}
          disabled={loading || !hasLocation}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-grove-300 rounded-lg text-bark-700 hover:bg-grove-50 disabled:opacity-50 transition-all"
        >
          {loading ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          {tenants.length > 0 ? "Refresh" : "Find nearby"}
        </button>
      </div>

      <p className="text-xs text-bark-400">
        Trade-area context for the BOV writeup. These are nearby businesses,
        not occupants of this property — add real tenants on the Rent Roll
        tab.
      </p>

      {tenants.length > 0 && (
        <ul className="divide-y divide-grove-200">
          {tenants.map((t) => (
            <li
              key={t.place_id || t.name}
              className="py-2 flex items-baseline justify-between gap-3"
            >
              <span className="text-sm text-bark-700 font-medium">
                {t.name}
              </span>
              <span className="text-xs text-bark-400 capitalize">
                {prettyType(t.types)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Pick the most descriptive type token and humanize it. */
function prettyType(types: string[]): string {
  // Drop generic noise; prefer the first specific category.
  const generic = new Set([
    "establishment",
    "point_of_interest",
    "food",
    "store",
  ]);
  const specific = types.find((t) => !generic.has(t)) || types[0] || "";
  return specific.replace(/_/g, " ");
}
